"use client";
import SectionWrapper from "@cc/section-wrapper";
import {
  Button,
  ButtonProps,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { sxArrayUtil } from "@util/sx-helpers";
import { Dispatch, SetStateAction } from "react";
import {
  GridViewOutlined,
  KeyboardArrowDown,
  ListRounded,
} from "@mui/icons-material";
import { AllNewsFilterOptions } from "@/cms-helpers/api-helper.client";
import { useNewsSearchAndFilter } from "@c/newsMedia/allNews/news-action-section";

export interface FilterAndSortProps {
  view: "grid" | "list";
  setView: Dispatch<SetStateAction<"grid" | "list">>;
  organizations: string[];
  topics: string[];
  years: string[];
}

const FilterAndSort = (props: FilterAndSortProps) => {
  const { view, setView, organizations, topics, years } = props;
  const { filters, setSearchFilters } = useNewsSearchAndFilter();

  return (
    <SectionWrapper
      disablePassColorToChildren
      direction={"row"}
      alignItems={"center"}
      py={3}
      SectionProps={{
        bgcolor: "common.white",
      }}
    >
      <Stack
        direction={{ lg: "row" }}
        width={1}
        alignItems={"center"}
        columnGap={3}
        rowGap={2}
        height={{ xs: "fit-content", lg: 48 }}
      >
        <Typography
          variant={"subtitle2"}
          component={"span"}
          color={"grey.600"}
          height={"min-content"}
          display={{
            xs: "none",
            lg: "block",
          }}
        >
          Filters
        </Typography>
        <Select
          variant={"outlined"}
          color={"primary"}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          defaultValue={filters.organization}
          renderValue={(value) => {
            if (value === "") {
              return "Company / Trust";
            }
            return value as string;
          }}
          onChange={(e) => {
            setSearchFilters({
              organization: e.target.value as string,
            });
          }}
          sx={{
            borderRadius: 0,
            color: (theme) => theme.palette.primary.main,
            height: 48,
            width: {
              xs: 1,
              lg: 1 / 5.3,
            },
            minWidth: "fit-content",
          }}
        >
          <MenuItem value={""}>All</MenuItem>
          {organizations.map((item, index) => {
            return (
              <MenuItem
                sx={{ textTransform: "capitalize" }}
                key={`${item}-${index}`}
                value={item}
              >
                {item}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          variant={"outlined"}
          color={"primary"}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          defaultValue={filters.topic}
          renderValue={(value) => {
            if (value === "") {
              return "Topic";
            }
            return value as string;
          }}
          onChange={(e) => {
            setSearchFilters({
              topic: e.target.value as string,
            });
          }}
          sx={{
            borderRadius: 0,
            color: (theme) => theme.palette.primary.main,
            height: 48,
            width: 1 / 5.3,
            minWidth: "fit-content",
            display: {
              xs: "none",
              lg: "block",
            },
          }}
        >
          <MenuItem value={""}>All</MenuItem>
          {topics.map((item, index) => {
            return (
              <MenuItem key={`${item}-${index}`} value={item}>
                {item}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          variant={"outlined"}
          color={"primary"}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          defaultValue={filters.year}
          renderValue={(value) => {
            if (value === "") {
              return "Year";
            }
            return value as string;
          }}
          onChange={(e) => {
            setSearchFilters({
              year: e.target.value as string,
            });
          }}
          sx={{
            borderRadius: 0,
            color: (theme) => theme.palette.primary.main,
            height: 48,
            width: 1 / 11,
            minWidth: "fit-content",
            display: {
              xs: "none",
              lg: "block",
            },
          }}
        >
          <MenuItem value={""}>All</MenuItem>
          {years.map((item, index) => {
            return (
              <MenuItem key={`${item}-${index}`} value={item}>
                {item}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          variant={"outlined"}
          color={"primary"}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          defaultValue={filters.sort}
          renderValue={(value) => {
            if (value === "desc") {
              return "Sort By: Recent";
            } else {
              return "Sort By: Old";
            }
          }}
          onChange={(e) => {
            setSearchFilters({
              sort: e.target.value as AllNewsFilterOptions["sort"],
            });
          }}
          sx={{
            borderRadius: 0,
            ml: "auto",
            color: (theme) => theme.palette.primary.main,
            height: 48,
            width: {
              xs: 1,
              lg: 1 / 7.5,
            },
            minWidth: "fit-content",
          }}
        >
          <MenuItem value={"desc"}>Recent</MenuItem>
          <MenuItem value={"asc"}>Old</MenuItem>
        </Select>
        <ViewToggleButtons
          sx={{
            display: {
              xs: "none",
              lg: "flex",
            },
          }}
          view={view}
          setView={setView}
        />
      </Stack>
    </SectionWrapper>
  );
};

export default FilterAndSort;

const languages = ["grid", "list"] as const;

interface ViewToggleButtonsProps extends Omit<ButtonProps, "children"> {
  view: "grid" | "list";
  setView: Dispatch<SetStateAction<"grid" | "list">>;
}

const ViewToggleButtons = (props: ViewToggleButtonsProps) => {
  const { sx, view = "grid", setView, ...restButtonProps } = props;
  return (
    <Button
      sx={[
        (theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "inherit",
          width: 104,
          columnGap: 1.5,
          borderRadius: 0,
          position: "relative",
          color: "inherit",
          border: "1px solid",
          borderColor: theme.palette.primary.main,
          py: 0,
          pt: 0.25,
          px: 1.5,
          "&::before": {
            content: '""',
            position: "absolute",
            mt: "-3px",
            width: 0.36,
            height: 0.8,
            transform: `translateX(${view === languages[0] ? -12 : 124}%)`,
            background: theme.palette.primary.main,
            transition: "transform 0.2s ease-in-out",
          },
        }),
        ...sxArrayUtil(sx),
      ]}
      onClick={() => {
        setView((prev) => {
          if (prev === languages[0]) {
            return languages[1];
          }
          return languages[0];
        });
      }}
      {...restButtonProps}
    >
      <GridViewOutlined
        color={view === languages[0] ? "secondary" : "primary"}
        sx={{
          position: "relative",
          transition: "transform 0.2s ease-in-out",
        }}
      />
      <ListRounded
        color={view === languages[1] ? "secondary" : "primary"}
        sx={{
          position: "relative",
          transition: "transform 0.2s ease-in-out",
        }}
      />
    </Button>
  );
};
